import {
  ContestCalculatedGateway,
  ContestResult,
  ContestState,
  DateCodes
} from '@maxpreps/apiserviceclients/apiModels';
import { cdnOrigin } from '@maxpreps/network/src/origins';
import { SUPPORT_GAMECHANGER } from '@maxpreps/shared/constants/supportArticles';
import { getTeamSchedule } from '@maxpreps/shared/data/ghost-apis/gateways-api';
import { replaceLast } from '@maxpreps/shared/functions/primitiveHelpers/stringHelpers';
import { AppType } from '@maxpreps/shared/functions/utils/requestContextUtils';
import getCSRRewriteAttributes from '../getCSRRewriteAttributes';
import {
  getAdminEnterScoresLink,
  getAdminEnterStatsLink,
  getTeamUrl
} from './../../utils/navigation';
import { Page } from './../../utils/pages';
import serializeSchedule, { IClientContest } from './serializeSchedule';

export interface IScheduleCallout {
  text: string;
  href: string;
  as?: string;
  imgSrc?: string;
  contest: IClientContest;
}

const getContestCalloutInfo = (
  contest: IClientContest,
  teamId: string,
  allSeasonId: string,
  canonicalUrl: string,
  appType: AppType
): IScheduleCallout | undefined => {
  const {
    contestId,
    contestState,
    currentTeam,
    opponentTeam,
    dateCode,
    sportSeasonId,
    isGameChangerConnected,
    hasGameChangerImportedStats
  } = contest;

  const { isTeamTBA } = opponentTeam || {};

  const contestInPast =
    contestState === ContestState.Boxscore ||
    contestState === ContestState.ScoreNotReported;
  const isDateTba =
    dateCode === DateCodes.DateTBA || dateCode === DateCodes.DateTimeTBA;
  // We only want to point out tba times for games in the future
  const isTimeTba =
    !contestInPast &&
    (dateCode === DateCodes.TimeTBA || dateCode === DateCodes.DateTimeTBA);

  if (isTeamTBA || isDateTba || isTimeTba) {
    const missing: string[] = [];
    if (isDateTba) missing.push('Date');
    if (isTimeTba) missing.push('Time');
    if (isTeamTBA) missing.push('Opponent');

    const csrAttributes = getCSRRewriteAttributes(
      getTeamUrl(Page.Schedule_Edit, canonicalUrl, { contestId, admin: 1 }),
      teamId,
      sportSeasonId,
      allSeasonId,
      appType
    );

    return {
      text: `Add ${replaceLast(missing.join(', '), ', ', ' & ')}`,
      href: csrAttributes.href,
      as: csrAttributes.as,
      contest
    };
  }

  if (contestState === ContestState.ScoreNotReported) {
    return {
      text: 'Add Score',
      href: getAdminEnterScoresLink(
        teamId,
        allSeasonId,
        sportSeasonId,
        contestId
      ),
      contest
    };
  }

  const { hasStats, calculatedTeamContestResult, totalsNeedUpdate } =
    currentTeam || {};
  const isForfeit =
    calculatedTeamContestResult === ContestResult.WinByForfeit ||
    calculatedTeamContestResult === ContestResult.LossByForfeit ||
    calculatedTeamContestResult === ContestResult.DoubleForfeit;

  if (
    contestState === ContestState.Boxscore &&
    !isForfeit &&
    (totalsNeedUpdate || !hasStats)
  ) {
    return {
      text: totalsNeedUpdate ? 'Recalculate Stats' : 'Add Stats',
      href: getAdminEnterStatsLink(
        teamId,
        allSeasonId,
        sportSeasonId,
        contestId
      ),
      contest
    };
  }

  if (isGameChangerConnected && !hasGameChangerImportedStats) {
    return {
      text: 'Import Stats',
      href: SUPPORT_GAMECHANGER,
      imgSrc: `${cdnOrigin}/includes/react/gamechanger.png`,
      contest
    };
  }
};

const getCalloutInfo = (
  contests: IClientContest[],
  teamId: string,
  allSeasonId: string,
  canonicalUrl: string,
  appType: AppType
): IScheduleCallout[] => {
  const callouts: IScheduleCallout[] = [];
  contests.forEach(contest => {
    if (contest && !contest.isDeleted) {
      const callout = getContestCalloutInfo(
        contest,
        teamId,
        allSeasonId,
        canonicalUrl,
        appType
      );
      if (callout) callouts.push(callout);
    }
  });
  return callouts;
};

// A wrapper so this formatted data is available outside of the schedule page
export const getCalloutInfoAsync = async (
  teamId: string,
  ssid: string,
  allSeasonId: string,
  canonicalUrl: string,
  appType: AppType
): Promise<IScheduleCallout[]> => {
  const [data, error] = await getTeamSchedule(teamId, ssid);

  if (error) return [];

  const serializedSchedule = serializeSchedule(
    data as ContestCalculatedGateway[],
    teamId
  );

  return getCalloutInfo(
    serializedSchedule,
    teamId,
    allSeasonId,
    canonicalUrl,
    appType
  );
};

export default getCalloutInfo;
